<template>
  <VModal :showing="showing" container-classes="w-192" data-testid="project-selector" @update:showing="$emit('close')">
    <template #header>
      <h2 class="text-lg font-medium text-gray-900 mb-0">Choose a Project</h2>
      <p class="text-sm text-gray-700 mb-0 capitalize">Create 360 Slide</p>
    </template>

    <template #default>
      <div v-if="hasNoScenes" class="text-gray-600 font-light text-center mt-12 mb-14">
        <p class="text-xl mb-4">No Projects created yet!</p>
        <p class="mb-0">Please contact your VRIFY support team<br />to track the completion of your Project</p>
      </div>
      <VSearchableTable
        v-else
        ref="tableRef"
        v-model:search="search"
        class="project-selector"
        :auto-load="true"
        :options="projectList"
        :current-item="projectOption"
        placeholder="Search by keyword"
        :disabled="disabled"
        :loading="loading"
        :count="count"
        @selected="onSelect"
      >
        <template #current="{ item }">
          <div>{{ item.name }}</div>
        </template>

        <template #headers>
          <th class="pl-6 w-32">&nbsp;</th>
          <th @click="sortOn('title')">
            Project Name
            <FontAwesomeIcon v-if="sortKey === 'title'" :icon="sortDir === '-' ? 'caret-up' : 'caret-down'" class="ml-2" />
          </th>

          <th class="pr-6 text-right" @click="sortOn('updated_at')">
            Last Updated
            <FontAwesomeIcon v-if="sortKey === 'updated_at'" :icon="sortDir === '-' ? 'caret-up' : 'caret-down'" class="ml-2" />
          </th>
        </template>

        <template #option="{ item }">
          <td class="pl-6">
            <img
              v-if="(item as unknown as Project).thumbnail"
              class="rounded-lg border border-gray-300 w-full"
              :src="(item as unknown as Project).thumbnail"
            />
            <PlaceholderDeck v-else class="w-32 rounded-lg" />
          </td>
          <td>
            <AccessPermissionBadge :value="(item as unknown as Project).view_access_type" />&ensp;{{ (item as unknown as Project).title }}
          </td>
          <td class="pr-6 text-right font-normal text-sm whitespace-nowrap">
            {{ formatDateTimeMed((item as unknown as Project).updated_at) }}
          </td>
        </template>
      </VSearchableTable>
    </template>

    <template #footer>
      <div class="flex justify-end gap-2"><VButton @click="$emit('close')">Back</VButton></div>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import PlaceholderDeck from '@/components/icons/deck-placeholder.svg'
import type { Project } from '@/entities/site'
import type { IndexSearchListing } from '@/entities/search'
import type { Option } from '@/components/VSearchableThumbnailList.vue'
import { debounce } from '@/lib/utils'
import { formatDateTimeMed } from '@/lib/datetime-format-utils'

const RESULT_LIMIT = 100

const props = defineProps({
  showing: {
    type: Boolean,
    default: false,
  },
  companyId: {
    type: [String, Number],
    required: true,
  },
  projectId: {
    type: Number as PropType<number | null>,
    default: null,
  },
  accessType: {
    type: String,
    default: 'edit',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:projectId', 'close'])

const search = ref('')
const loading = ref(true)
const projects = ref<Project[]>([])
const count = ref(0)
const hasError = ref(false)
const projectOption = ref<Project | null>(null)
const sortKey = ref('updated_at')
const sortDir = ref<'' | '-'>('-')
const tableRef = ref<any | undefined>()
const initialProjectList = ref<Project[]>([])
const projectList = computed(() => projects.value ?? [])
const hasNoScenes = computed(() => !loading.value && initialProjectList.value.length <= 0)

onMounted(async () => {
  await fetchProjects()
  initialProjectList.value = [...projects.value]
})

watch([sortKey, sortDir], ([key, dir], [oldKey, oldDir]) => {
  if (oldKey !== key || oldDir !== dir) fetchProjects()
})

// Fetch the project (if needed) so we can display the name
watch(
  () => props.projectId,
  async () => {
    if (props.projectId === projectOption.value?.id) {
      return
    }

    if (!props.projectId) {
      projectOption.value = null
      return
    }

    // Temporarily fill in this value until we get a response
    projectOption.value = { id: props.projectId } as any

    try {
      const data = await $fetch<Project>(`v2/projects/${props.projectId}`, {
        ...useFetchSharedOptions(),
      })

      if (props.projectId === data.id) {
        projectOption.value = data
      }
    } catch (e) {
      hasError.value = true
    }
  },
  { immediate: true },
)

const debouncedFetch = debounce(fetchProjects, 500)
watch([() => props.companyId, search], () => {
  loading.value = true // Set loading to true immediately
  debouncedFetch()
})

let identifier: string
async function fetchProjects() {
  loading.value = true
  projects.value = []
  count.value = 0
  hasError.value = false

  let thisIdentifier: string

  // Cache values that determine response so we can confirm we're still valid later
  identifier = thisIdentifier = `${props.companyId}:${search.value}:${sortDir.value}${sortKey.value}`
  try {
    const data = await $fetch<IndexSearchListing<Project>>(`v2/companies/${props.companyId}/projects`, {
      ...useFetchSharedOptions(),
      params: {
        limit: RESULT_LIMIT,
        archived: false,
        sort_by: `${sortDir.value}${sortKey.value}`,
        query: search.value,
      },
    })

    if (identifier === thisIdentifier) {
      projects.value = data.results
      count.value = data.count ?? 0

      // Make sure selected value is within viewport
      nextTick(() => {
        tableRef.value?.$el.querySelector('.selected')?.scrollIntoView({ block: 'center' })
      })
    }
  } catch (e) {
    hasError.value = true
  } finally {
    if (identifier === thisIdentifier) {
      loading.value = false
    }
  }
}

function sortOn(key: string) {
  if (key === sortKey.value) {
    sortDir.value = sortDir.value === '' ? '-' : ''
    return
  }
  sortKey.value = key
  sortDir.value = ''
}

function onSelect(selectedProject: Project): void {
  // Save the selected project so we can avoid fetching it later
  projectOption.value = selectedProject
  emit('update:projectId', selectedProject.id)
}
</script>

<style lang="stylus" scoped>
.project-selector
  & :deep(th)
    @apply text-xs uppercase text-gray-700 bg-gray-50 px-4 py-3 font-medium border-l-0 cursor-pointer
  & :deep(td)
    @apply text-gray-800 p-4 border-0
  & :deep(tbody tr:nth-child(even) td)
    @apply bg-gray-50
  & :deep(tbody tr:hover td)
    @apply bg-gray-200
  .option img
    aspect-ratio 16/9
    object-fit cover
</style>
